import {useQuery} from "@apollo/client";
import DOMPurify from "dompurify";
import {Modal} from "react-bootstrap";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {shortDateUser} from "@/lib/format.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {ChangelogResults, GQL_CHANGELOG} from "@/lib/queries/website.ts";

import styles from "./WhatsNewModal.module.scss";

const WhatsNewBody = () => {
  const {user} = useCurrentUserContext();
  const result = useQuery<ChangelogResults>(GQL_CHANGELOG);

  if (!result.data) {
    return <LoadingContainer text="Loading..." />;
  }

  const entries = result.data?.changelog ?? [];

  return (
    <>
      {entries?.map((entry, index) => (
        <>
          <div>
            <div className="d-flex align-items-center gap-2 pulseTextH3Bold pb-3">
              <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(entry.title)}} />
              {!entry.isPublished && <div className={styles.draft}>*Draft*</div>}
            </div>
            <div className="pb-3 pulse-color-gray-700">{shortDateUser(entry.date.toString(), user!)}</div>
            <div
              className={styles.whatsNewContainer}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(entry.content, {
                  ADD_ATTR: ["target"], // Allow target attribute
                }),
              }}
            />
          </div>
          {index < entries.length - 1 && <Divider className={"pb-3"} />}
        </>
      ))}
    </>
  );
};

interface WhatsNewModalProps {
  show: boolean;
  onCancel: () => void;
}

export const WhatsNewModal = ({show, onCancel}: WhatsNewModalProps) => (
  <Modal onHide={onCancel} show={show} centered backdrop={"static"} size={"lg"}>
    <Modal.Header closeButton className="px-lg-4">
      <Modal.Title>What's New</Modal.Title>
    </Modal.Header>
    <Modal.Body className="px-lg-4">
      <UpErrorBoundary>
        <WhatsNewBody />
      </UpErrorBoundary>
    </Modal.Body>
    <Modal.Footer>
      <Button
        as={"a"}
        variant={"secondary"}
        referrerPolicy="no-referrer"
        target="_blank"
        href="https://uptime.featurebase.app/changelog"
      >
        View Release Notes
      </Button>
      <Button onClick={onCancel} data-bs-dismiss="modal">
        Dismiss
      </Button>
    </Modal.Footer>
  </Modal>
);
